import type { IEnvelope, IHttpClient } from '@/ai-text-lib/client';
import {
    DEFAULT_WAIT_SEC,
    ENDPOINT_GENERATE,
    ENDPOINT_STATUS,
    JOB_TOKEN_HEADER,
    POLL_BACKOFF_MS,
    POLL_BASE_MS,
    POLL_MAX_MS,
} from '../const';
import type {
    IPoemClientOptions,
    IPoemInput,
    IStanza,
    TPoemFailure,
    TPoemState,
} from './types';

// the person's own inputs: a refusal of one is theirs to fix, not ours to report
const INPUT_FIELDS = ['prompt', 'toName', 'fromName'];

const defaultSleep = (ms: number): Promise<void> =>
    new Promise((resolve) => setTimeout(resolve, ms));

const numberOrUndef = (value: unknown): Maybe<number> =>
    typeof value === 'number' && Number.isFinite(value) ? value : undefined;

/** `[{id, text}]` from the status answer, or null when it is not that shape. */
export function parseStanzas(value: unknown): IStanza[] | null {
    if (!Array.isArray(value) || value.length === 0) {
        return null;
    }

    const stanzas: IStanza[] = [];
    for (const item of value) {
        const { id, text } = (item ?? {}) as Record<string, unknown>;
        if (typeof id !== 'number' || typeof text !== 'string') {
            return null;
        }
        stanzas.push({ id, text });
    }

    return stanzas;
}

/**
 * Enqueue → poll for POST /api/ai/poem (doc/ai/poem-generation.md), one poem at a
 * time: a new generate() bumps `runId`, and an older poll loop that sees the
 * mismatch drops out without touching the state.
 *
 * The wait is the server's (`waitSeconds`): the same number sets how long a job
 * may queue there, and a client with its own would give up on a poem still
 * being written, or keep polling one already shed.
 */
export class PoemClient {
    private readonly http: IHttpClient;
    private readonly onChange: (state: TPoemState) => void;
    private readonly sleep: (ms: number) => Promise<void>;
    private readonly now: () => number;

    private state: TPoemState = { phase: 'idle' };
    private runId = 0;

    constructor(options: IPoemClientOptions) {
        this.http = options.http;
        this.onChange = options.onChange;
        this.sleep = options.sleep ?? defaultSleep;
        this.now = options.now ?? (() => Date.now());
    }

    getState(): TPoemState {
        return this.state;
    }

    isBusy(): boolean {
        return this.state.phase === 'submitting' || this.state.phase === 'queued';
    }

    async generate(input: IPoemInput): Promise<void> {
        const myRun = ++this.runId;
        const startedAt = this.now();
        this.set({ phase: 'submitting' });

        let res;
        try {
            res = await this.http.post(ENDPOINT_GENERATE, input);
        } catch {
            return this.fail(myRun, 'unavailable');
        }
        if (myRun !== this.runId) {
            return;
        }

        const { status, body, retryAfter } = res;
        const waitMs = (numberOrUndef(body.waitSeconds) ?? DEFAULT_WAIT_SEC) * 1000;

        // 202 – queued; 409 – a poem of this person is already being written:
        // follow it instead of starting another
        if ((status === 202 || status === 409) && body.jobId) {
            return this.poll(myRun, String(body.jobId), startedAt + waitMs);
        }
        // another poem of this visitor (another tab, an old description) is
        // running: nothing of ours to follow, ask again once it is done
        if (status === 409) {
            return this.fail(myRun, 'busy', retryAfter);
        }
        if (status === 429) {
            return this.commit(myRun, {
                phase: 'error',
                reason: 'limit',
                limit: typeof body.reason === 'string' ? body.reason : undefined,
                retryAfter,
            });
        }
        if (status === 403) {
            return this.fail(myRun, 'off');
        }
        if (
            status === 400 &&
            typeof body.field === 'string' &&
            INPUT_FIELDS.includes(body.field)
        ) {
            return this.commit(myRun, {
                phase: 'error',
                reason: 'input',
                field: body.field,
            });
        }
        if (status === 400 || status === 404) {
            return this.commit(myRun, {
                phase: 'error',
                reason: 'rejected',
                field: typeof body.field === 'string' ? body.field : undefined,
            });
        }

        return this.fail(myRun, 'unavailable', retryAfter);
    }

    private async poll(
        myRun: number,
        token: string,
        deadline: number
    ): Promise<void> {
        this.set({ phase: 'queued' });
        let interval = POLL_BASE_MS;

        while (this.now() + interval <= deadline) {
            await this.sleep(interval);
            if (myRun !== this.runId) {
                return;
            }

            let res;
            try {
                res = await this.http.get(ENDPOINT_STATUS, {
                    [JOB_TOKEN_HEADER]: token,
                });
            } catch {
                res = null;
            }
            if (myRun !== this.runId) {
                return;
            }

            // a dropped poll or a 502 from a saturated pool is not a failed poem:
            // the job is still being written, and a failure here would open the
            // "write it yourself" fields over a poem about to arrive
            if (res && this.applyStatus(myRun, res.status, res.body)) {
                return;
            }

            interval = Math.min(interval + POLL_BACKOFF_MS, POLL_MAX_MS);
        }

        return this.fail(myRun, 'timeout');
    }

    /** Returns true when a terminal state was committed. */
    private applyStatus(myRun: number, status: number, body: IEnvelope): boolean {
        // 404: the job is gone; 403: the poem was switched off. Anything else
        // not 200 is the road, not the answer – poll again
        if (status === 404 || status === 403) {
            this.fail(myRun, status === 403 ? 'off' : 'unavailable');
            return true;
        }
        if (status !== 200) {
            return false;
        }

        switch (body.status) {
            case 'done': {
                const stanzas = parseStanzas(body.stanzas);
                if (stanzas) {
                    this.commit(myRun, { phase: 'done', stanzas });
                } else {
                    this.fail(myRun, 'unavailable');
                }
                return true;
            }
            case 'failed':
                this.fail(myRun, 'failed');
                return true;
            case 'pending':
                this.commit(myRun, {
                    phase: 'queued',
                    queuePosition: numberOrUndef(body.queuePosition),
                    estimatedWaitSeconds: numberOrUndef(body.estimatedWaitSeconds),
                });
                return false;
            default: // processing: the worker is writing it
                this.commit(myRun, { phase: 'queued' });
                return false;
        }
    }

    private fail(myRun: number, reason: TPoemFailure, retryAfter?: number): void {
        this.commit(myRun, { phase: 'error', reason, retryAfter });
    }

    private commit(myRun: number, state: TPoemState): void {
        if (myRun !== this.runId) {
            return;
        }
        this.set(state);
    }

    private set(state: TPoemState): void {
        this.state = state;
        this.onChange(state);
    }
}
